import { OnrampApiError } from '../client.js';
export function watchOperation(options) {
    const interval = options.pollSeconds ?? 7;
    if (interval < 5 || interval > 10 || !Number.isInteger(interval))
        throw new Error('onramp_invalid_poll_interval');
    let active = true;
    let disposed = false;
    let terminal = false;
    let recoveryPending = false;
    let version = 0;
    let failures = 0;
    let nextAllowedRefresh = 0;
    let timer;
    let request;
    let refreshQueued = false;
    let unsubscribe;
    try {
        unsubscribe = options.subscribe((id) => {
            if (id === options.operationId)
                refresh();
        });
    }
    catch {
        options.onError('onramp_refresh_failed');
    }
    refresh();
    return {
        refresh,
        setActive(value) {
            active = value;
            clearTimeout(timer);
            if (!active)
                request?.abort();
            else
                refresh();
        },
        dispose() {
            disposed = true;
            clearTimeout(timer);
            request?.abort();
            unsubscribe?.();
        },
    };
    function refresh() {
        if (!active || disposed)
            return;
        if (Date.now() < nextAllowedRefresh) {
            clearTimeout(timer);
            timer = setTimeout(refresh, nextAllowedRefresh - Date.now());
            return;
        }
        clearTimeout(timer);
        if (request) {
            refreshQueued = true;
            return;
        }
        const controller = new AbortController();
        request = controller;
        void load(controller);
    }
    async function load(controller) {
        try {
            const snapshot = await options.fetchOperation(controller.signal);
            if (controller.signal.aborted || disposed || !active)
                return;
            if (snapshot.id !== options.operationId)
                throw new Error('onramp_operation_mismatch');
            failures = 0;
            nextAllowedRefresh = 0;
            recoveryPending = false;
            if (snapshot.version > version) {
                version = snapshot.version;
                terminal =
                    ['failed', 'cancelled', 'refunded'].includes(snapshot.status) ||
                        (snapshot.status === 'delivered' &&
                            (snapshot.walletDelivery?.state === 'confirmed' ||
                                snapshot.walletDelivery?.state === 'sandbox_confirmed'));
                options.onUpdate(snapshot);
            }
        }
        catch (error) {
            if (!controller.signal.aborted && !disposed && active) {
                failures += 1;
                const backoff = Math.min(60000, interval * 1000 * 2 ** Math.min(failures - 1, 4));
                const retryAfter = error instanceof OnrampApiError ? error.retryAfterMs : 0;
                nextAllowedRefresh = Date.now() + Math.max(backoff, retryAfter);
                recoveryPending = true;
                options.onError('onramp_refresh_failed');
            }
        }
        finally {
            request = undefined;
            const queued = refreshQueued;
            refreshQueued = false;
            if (!disposed && active) {
                if (queued)
                    refresh();
                else if (!terminal || recoveryPending)
                    timer = setTimeout(refresh, Math.max(interval * 1000, nextAllowedRefresh - Date.now()));
            }
        }
    }
}
